import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { APP_NAME, SIDEBAR_LINKS } from '../utils';
import { pageList } from '../utils/gestion-access';

const isLinkActive = (pathname, url) => {
  if (url === '/') return pathname === '/';
  if (url === '/personnel') {
    return pathname === '/personnel' || (pathname.startsWith('/personnel/') && !pathname.startsWith('/personnel/paie'));
  }
  return pathname === url || pathname.startsWith(`${url}/`);
};

const Sidebar = () => {
  const { pathname } = useLocation();
  const user = useSelector((state) => state.auth.user);

  const getSidebarLinks = (posteId, role) => {
    if (role === 'superAdmin') return SIDEBAR_LINKS;

    let filteredLinks = [...SIDEBAR_LINKS];

    const hideStaffMenu = (link) =>
      link.url === '/rapports' ||
      link.url === '/finance-impots' ||
      (!['Ressources humaines', 'Analytique'].includes(link.category) &&
        !['Carraux cassé', 'Liste de vente carraux cassé', 'Vente carraux cassé'].includes(link.text));

    if (Number(user?.personnels?.[0]?.poste_id) === 13) {
      filteredLinks = filteredLinks.filter(hideStaffMenu);
    } else if (role === 'Administrateur') {
      filteredLinks = filteredLinks.filter(
        (link) => link.url === '/personnel' || link.url === '/personnel/paie' || link.url === '/personnel/paie/reglages' || hideStaffMenu(link)
      );
    }

    switch (posteId) {
      case 1:
        filteredLinks = filteredLinks.filter(
          (link) =>
            link.url === '/rapports' ||
            !['Gestion des produits', 'Analytique', 'Ressources humaines', 'Administration'].includes(
              link.category
            )
        );
        break;
      case 2:
        filteredLinks = filteredLinks.filter(
          (link) =>
            !['Gestion des produits', 'Ressources humaines', 'Administration'].includes(link.category)
        );
        break;
      case 4:
        filteredLinks = filteredLinks.filter(
          (link) => !['Gestion des ventes', 'Administration'].includes(link.category)
        );
        break;
      case 6:
        filteredLinks = filteredLinks.filter(
          (link) => !['Gestion des produits', 'Gestion des ventes'].includes(link.category)
        );
        break;
      case 8:
        filteredLinks = filteredLinks.filter((link) =>
          ['/rapports', '/finance-impots'].includes(link.url)
        );
        break;
      default:
        break;
    }

    const rapportsLink = SIDEBAR_LINKS.find((link) => link.url === '/rapports');
    if (rapportsLink && !filteredLinks.some((link) => link.url === '/rapports')) {
      filteredLinks = [...filteredLinks, rapportsLink];
    }

    const financeLink = SIDEBAR_LINKS.find((link) => link.url === '/finance-impots');
    if (financeLink && !filteredLinks.some((link) => link.url === '/finance-impots')) {
      filteredLinks = [...filteredLinks, financeLink];
    }

    return filteredLinks;
  };

  const storedPages = pageList(user);
  let sidebarLinksToDisplay = user?.role === 'superAdmin' || !storedPages
    ? getSidebarLinks(user?.personnels?.[0]?.poste_id, user?.role)
    : SIDEBAR_LINKS.filter((link) => storedPages.includes(link.url));

  if (user?.role === 'superAdmin' && !sidebarLinksToDisplay.some((link) => link.url === '/parametres/acces')) {
    sidebarLinksToDisplay = [
      ...sidebarLinksToDisplay,
      {
        id: 'parametres-acces',
        icon: 'bi bi-sliders',
        text: 'Accès aux pages',
        url: '/parametres/acces',
        category: 'Paramètres',
      },
    ];
  }

  const groupedLinks = sidebarLinksToDisplay.reduce((acc, link) => {
    if (!acc[link.category]) acc[link.category] = [];
    acc[link.category].push(link);
    return acc;
  }, {});

  const firstName = user?.name?.split(' ')[0] ?? 'Utilisateur';
  const roleLabel = user?.role ?? user?.personnels?.[0]?.poste_id ?? '';

  return (
    <aside id="sidebar" className="sidebar duo-sidebar">
      <div className="duo-sidebar__profile">
        <div className="duo-sidebar__avatar" aria-hidden>
          {firstName.charAt(0).toUpperCase()}
        </div>
        <div className="duo-sidebar__profile-text">
          <p className="duo-sidebar__name">{user?.name ?? firstName}</p>
          <p className="duo-sidebar__role">{roleLabel}</p>
        </div>
      </div>

      <p className="duo-sidebar__app">{APP_NAME}</p>

      <nav className="duo-sidebar__nav" aria-label="Menu principal">
        {Object.entries(groupedLinks).map(([category, links]) => (
          <section key={category} className="duo-sidebar__section">
            <h3 className="duo-sidebar__category">{category}</h3>
            <ul className="duo-sidebar__list">
              {links.map((link) => {
                const active = isLinkActive(pathname, link.url);
                return (
                  <li key={link.id}>
                    <Link
                      to={link.url}
                      className={`duo-sidebar__link${active ? ' is-active' : ''}`}
                      aria-current={active ? 'page' : undefined}
                    >
                      <span className="duo-sidebar__link-icon" aria-hidden>
                        <i className={link.icon} />
                      </span>
                      <span className="duo-sidebar__link-label">{link.text}</span>
                      {active ? <span className="duo-sidebar__link-dot" aria-hidden /> : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;
